// The time zones offered for a hospital on the Hospitals page.
//
// Values are IANA zone NAMES, never offsets or abbreviations: the backend rejects anything else
// (backend/src/utils/local-time.ts), because "UTC-6" or "CST" is an hour wrong for half the year.
// Inbound feeds that send local wall-clock times (HL7, case files, shift exports) use the stored
// zone to place a hospital's "07:30" in UTC.
//
// A short US list is deliberate — every customer today is a US anesthesia group. A hospital whose
// stored zone is not on the list (set through the API) is still shown, by its raw name, rather
// than being silently displayed as "Not set".
export interface HospitalTimezoneOption {
  value: string;
  label: string;
}

export const US_HOSPITAL_TIMEZONES: readonly HospitalTimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona — no daylight saving (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

/** The select options for a hospital whose stored zone is `current` — the US list, plus `current` if it is not on it. */
export function timezoneOptionsFor(current?: string): HospitalTimezoneOption[] {
  if (!current || US_HOSPITAL_TIMEZONES.some((option) => option.value === current)) {
    return [...US_HOSPITAL_TIMEZONES];
  }
  return [...US_HOSPITAL_TIMEZONES, { value: current, label: current }];
}

/** A human label for a stored zone: the friendly name for a listed zone, the raw name otherwise. */
export function timezoneLabel(zone?: string): string {
  if (!zone) return 'Not set';
  return US_HOSPITAL_TIMEZONES.find((option) => option.value === zone)?.label ?? zone;
}
